import TextArea from "@/components/atoms/TextArea";
import { DatePicker } from "@/components/atoms/DatePicker";
import Button from "@/components/atoms/Button";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import Notice  from "@/types/Notice";
import { Switch } from "@/components/atoms/Switch";
import NoticeSchema from "@/schemas/notice";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { CheckboxButton } from "@/components/atoms/Checkbox";
import Link from "@/components/atoms/Link";
import { capitalize } from "@/lib/utils";
import ErrorType from "@/types/ErrorType";

type Props = {
  mode?: "create" | "edit";
  notice: Notice | null;
  edit: boolean;
  onSubmit: (data: any) => void;
  errors: ErrorType;
};

const NoticeForm = ({ mode = "edit", edit, onSubmit, notice, errors }: Props) => {

  const form = useForm({
    resolver: zodResolver(NoticeSchema),
    defaultValues: notice
      ? {
          ...notice,
          hide_from_client: notice.hide_from_client ? true : false,
          notice_active: notice.notice_active ? true : false
        }
      : {
          notice_message: "",
          notice_active: false,
          start_date: "",
          end_date: "",
          hide_from_client: false
        },

  });

  const handleSubmit = (data: any) =>
    onSubmit(data);

  useEffect(() => {
    if (!edit) {
      form.reset({ ...notice });
    }
  }, [edit]);

  useEffect(() => {
    if (errors) {
      Object.entries(errors).forEach(([key, messages]) => {
        form.setError(key as keyof typeof form.getValues, {
          type: "manual",
          message: `${capitalize(key)} ${messages[0]}`,
        });
      });
    }
  }
  , [errors]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="notice_message"
          render={({ field }) => (
            <FormItem className="w-1/2">
              <FormLabel>Message*</FormLabel>
              <FormControl>
                <TextArea
                  {...field}
                  value={field.value ?? ""}
                  placeholder="Message"
                  disabled={!edit}
                  className="w-full p-2 border dark:border-border rounded-md shadow-sm mb-2"
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-wrap gap-3 mt-2">
          <FormField
            control={form.control}
            name="start_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Start Date</FormLabel>
                <FormControl>
                  <DatePicker placeholder="Start Date" value={field.value ? new Date(field.value) : undefined} onChange={field.onChange} disabled={!edit}/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="end_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>End Date</FormLabel>
                <FormControl>
                  <DatePicker placeholder="End Date" value={field.value ? new Date(field.value) : undefined} onChange={field.onChange} disabled={!edit}/>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="w-44">
          <FormField
            control={form.control}
            name="hide_from_client"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <CheckboxButton
                    disabled={!edit}
                    checked={field.value === true}
                    onCheckedChange={(value) => field.onChange(value === true)}
                  >
                    Hide From Client
                  </CheckboxButton>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="notice_active"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status*</FormLabel>
              <FormControl>
                <div>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) =>
                      field.onChange(
                        checked
                          ? true
                          : false
                      )
                    }
                    disabled={!edit}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        

        
        <div className="flex gap-2">
          {mode === "create" && (
            <Link href="/notices">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
          )}
          <Button type="submit" disabled={!edit}>
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default NoticeForm;
